/**
 * GlobalA11yEnhancer — sitewide WCAG 2.1 AA safety net (SEO BATCH C)
 *
 * Runtime DOM enhancer that fixes common a11y gaps on EVERY route without
 * touching individual components:
 *   1. Icon-only <button>/<a> elements with no accessible name get an
 *      aria-label derived from `title`, the inner <svg aria-label>, the
 *      lucide icon class (e.g. `lucide-x` → "Close"), or a sensible
 *      fallback. Author-set aria-label / aria-labelledby / text content
 *      are NEVER overridden.
 *   2. Decorative <svg> children of an enhanced control get aria-hidden.
 *
 * Pure progressive enhancement: zero deps, no React renders, runs after
 * mount + on MutationObserver. The observer only inspects newly-added
 * subtrees (not full document re-walks), is debounced (~200ms) via
 * requestIdleCallback, and processed elements are flagged via
 * dataset.a11yEnhanced so each element is touched at most once.
 */
import { useEffect } from 'react';

const ICON_NAME_MAP: Record<string, string> = {
  'lucide-x': 'Close',
  'lucide-menu': 'Open menu',
  'lucide-chevron-up': 'Scroll to top',
  'lucide-chevron-down': 'Expand',
  'lucide-chevron-left': 'Previous',
  'lucide-chevron-right': 'Next',
  'lucide-arrow-left': 'Previous',
  'lucide-arrow-right': 'Next',
  'lucide-search': 'Search',
  'lucide-message-circle': 'Open chat',
  'lucide-phone': 'Call',
  'lucide-mail': 'Email',
  'lucide-share-2': 'Share',
  'lucide-play': 'Play',
  'lucide-pause': 'Pause',
  'lucide-plus': 'Add',
  'lucide-minus': 'Remove',
  'lucide-trash': 'Delete',
  'lucide-trash-2': 'Delete',
  'lucide-edit': 'Edit',
  'lucide-settings': 'Settings',
  'lucide-user': 'Account',
  'lucide-shopping-cart': 'Cart',
  'lucide-heart': 'Favorite',
  'lucide-bookmark': 'Bookmark',
};

function hasAccessibleName(el: HTMLElement): boolean {
  if (el.getAttribute('aria-label')?.trim()) return true;
  if (el.getAttribute('aria-labelledby')?.trim()) return true;
  if (el.getAttribute('title')?.trim()) return true;
  const txt = (el.textContent || '').replace(/\s+/g, '').trim();
  if (txt.length > 0) return true;
  const img = el.querySelector('img[alt]') as HTMLImageElement | null;
  if (img && img.alt.trim()) return true;
  return false;
}

function deriveLabel(el: HTMLElement): string | null {
  const title = el.getAttribute('title');
  if (title?.trim()) return title.trim();
  const svg = el.querySelector('svg');
  if (svg) {
    const svgAria = svg.getAttribute('aria-label');
    if (svgAria?.trim()) return svgAria.trim();
    const cls = svg.getAttribute('class') || '';
    for (const key of Object.keys(ICON_NAME_MAP)) {
      if (cls.split(/\s+/).includes(key)) return ICON_NAME_MAP[key];
    }
  }
  const href = el.getAttribute('href') || '';
  if (href.startsWith('tel:')) return 'Call';
  if (href.startsWith('mailto:')) return 'Email';
  if (/wa\.me|whatsapp/i.test(href)) return 'Open WhatsApp';
  if (/facebook\.com/i.test(href)) return 'Facebook';
  if (/twitter\.com|x\.com/i.test(href)) return 'Twitter';
  if (/linkedin\.com/i.test(href)) return 'LinkedIn';
  if (/instagram\.com/i.test(href)) return 'Instagram';
  if (/youtube\.com|youtu\.be/i.test(href)) return 'YouTube';
  return null;
}

function enhanceOne(el: HTMLElement) {
  if (el.dataset.a11yEnhanced === '1') return;
  if (hasAccessibleName(el)) {
    el.dataset.a11yEnhanced = '1';
    return;
  }
  const label = deriveLabel(el);
  if (label) el.setAttribute('aria-label', label);
  el.querySelectorAll('svg:not([aria-label]):not([role="img"])').forEach((s) => {
    if (!s.hasAttribute('aria-hidden')) s.setAttribute('aria-hidden', 'true');
  });
  el.dataset.a11yEnhanced = '1';
}

function enhanceSubtree(root: ParentNode) {
  const nodes = root.querySelectorAll<HTMLElement>('button, a, [role="button"]');
  nodes.forEach(enhanceOne);
}

type IdleScheduler = (cb: () => void) => number;
const scheduleIdle: IdleScheduler =
  typeof window !== 'undefined' && (window as any).requestIdleCallback
    ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 500 })
    : (cb) => window.setTimeout(cb, 200);

export function GlobalA11yEnhancer() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Initial pass on the existing DOM (deferred to idle).
    let initialTimer = window.setTimeout(() => scheduleIdle(() => enhanceSubtree(document.body)), 200);

    // Buffer newly-added subtrees only; never re-walk the whole document on each mutation.
    const pendingRoots: ParentNode[] = [];
    const pendingEls: HTMLElement[] = [];
    let scheduled = false;
    let debounceTimer = 0;

    const flush = () => {
      scheduled = false;
      const roots = pendingRoots.splice(0, pendingRoots.length);
      const els = pendingEls.splice(0, pendingEls.length);
      for (const r of roots) enhanceSubtree(r);
      for (const e of els) enhanceOne(e);
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => scheduleIdle(flush), 200);
    };

    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          // Buffer the new subtree root; enhanceSubtree() handles descendants.
          // Also enqueue the element itself in case it's a button/a/[role=button].
          pendingEls.push(node);
          pendingRoots.push(node);
        });
      }
      if (pendingRoots.length || pendingEls.length) schedule();
    });

    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      window.clearTimeout(initialTimer);
      window.clearTimeout(debounceTimer);
    };
  }, []);
  return null;
}

export default GlobalA11yEnhancer;
